import classNames from 'classnames';
import React from 'react';

import Filter from 'sharedreact/data/Filter';
import FilterRule from 'sharedreact/data/FilterRule';
import { translate } from 'sharedreact/utils/LanguageSupport';

function _getSearchText (state: State, value: string): string {
  return state.searchText != null ? state.searchText : value;
}

type Props = {
  id: string,
  className?: string,
  value: string,
  keywords: string[],
  readOnly?: boolean,
  highlight?: boolean,
  alignRight?: boolean,

  onSearch: (searchString: string) => void,
  onFocus?: () => void,
  onBlur?: () => void
};

type State = {
  cachedSearchTextProps: string | null,
  searchText: string | null,
  hasFocus: boolean,
  showDropDown: boolean,
  keyboardSelection: number,
  ignoreSetChange: boolean
};

class SearchField extends React.PureComponent<Props, State> {
  private _intervalId: NodeJS.Timeout | null;
  private _intervalId2: NodeJS.Timeout | null;

  constructor (props: Props) {
    super(props);

    this._intervalId = null;
    this._intervalId2 = null;

    this.state = {
      cachedSearchTextProps: null,
      searchText: null,
      hasFocus: false,
      showDropDown: false,
      keyboardSelection: -1,
      ignoreSetChange: false
    };

    this.handleSearchStringChange = this.handleSearchStringChange.bind(this);
    this.handleKeyPressed = this.handleKeyPressed.bind(this);
    this.handleGetFocus = this.handleGetFocus.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleFocusLost = this.handleFocusLost.bind(this);
    this.handleDropdownGetFocus = this.handleDropdownGetFocus.bind(this);
    this.fixDropDownSize = this.fixDropDownSize.bind(this);
  }

  override componentWillUnmount (): void {
    window.removeEventListener('resize', this.fixDropDownSize);

    if (this.state.hasFocus && this.props.onBlur != null) {
      this.props.onBlur();
    }

    if (this._intervalId != null) {
      clearTimeout(this._intervalId);
    }
    this._intervalId = null;

    if (this._intervalId2 != null) {
      clearTimeout(this._intervalId2);
    }
    this._intervalId2 = null;
  }

  override componentDidMount (): void {
    this.fixDropDownSize();
    window.addEventListener('resize', this.fixDropDownSize);
  }

  override componentDidUpdate (prevProps: Readonly<Props>): void {
    if (prevProps.keywords !== this.props.keywords) {
      this.fixDropDownSize();
    }
  }

  // synchronize the state before rerendering
  // added for when user revisits the page and the search field is cleared
  static getDerivedStateFromProps (nextProps: Props, prevState: State) {
    if (nextProps.value !== prevState.cachedSearchTextProps) {
      return { cachedSearchTextProps: nextProps.value, searchText: nextProps.value };
    }
    return null;
  }

  checkSearchFieldIsIdle (oldSearchFieldString: string): void {
    if (this.state.searchText === oldSearchFieldString) {
      this.handleChanged();
    }

    this._intervalId = null;
  }

  handleSearchStringChange (event: React.ChangeEvent<HTMLInputElement>): void {
    if (typeof event.target.value === 'string') {
      const newString: string = event.target.value;

      if (newString.length) {
        const filter: Filter = Filter.createFromString(newString, this.props.keywords);

        this.setState({
          searchText: newString,
          showDropDown: !filter.hasLabels()
        });
      } else {
        this.setState({
          searchText: newString,
          showDropDown: false,
          keyboardSelection: -1
        });
      }

      if (this._intervalId != null) {
        clearTimeout(this._intervalId);
      }

      this._intervalId = setTimeout(
        () => this.checkSearchFieldIsIdle(newString),
        1500
      );
    }
  }

  clearSearchText (): void {
    this.props.onSearch('');
    this.setState({
      searchText: null,
      showDropDown: false,
      keyboardSelection: -1
    });
  }

  handleChanged<K extends keyof State> (stateChanges: Pick<State, K> | null = null): void {
    const searchString: string = _getSearchText(this.state, this.props.value);
    this.props.onSearch(searchString);

    if (stateChanges) {
      this.setState(stateChanges);
    }
  }

  handleKeyPressed (event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      if (this.state.keyboardSelection !== -1) {
        this.selectKeyword(this.props.keywords[this.state.keyboardSelection]);
      } else {
        this.handleChanged({ showDropDown: false, keyboardSelection: -1 });

        if (this._intervalId != null) {
          clearTimeout(this._intervalId);
          this._intervalId = null;
        }
      }
    } else if (event.key === 'ArrowDown') {
      if (this.state.showDropDown) {
        this.setState((prevState: State, props: Props) => {
          if (prevState.keyboardSelection + 1 < this.props.keywords.length) {
            return { keyboardSelection: prevState.keyboardSelection + 1 };
          } else {
            return { keyboardSelection: 0 };
          }
        });
      }
    } else if (event.key === 'ArrowUp') {
      if (this.state.showDropDown) {
        this.setState((prevState: State, props: Props) => {
          if (prevState.keyboardSelection > 0) {
            return { keyboardSelection: prevState.keyboardSelection - 1 };
          } else {
            return { keyboardSelection: this.props.keywords.length - 1 };
          }
        });
      }
    }
  }

  handleGetFocus (event: React.FocusEvent<HTMLInputElement>): void {
    this.setState({ hasFocus: true });

    if (this.props.onFocus != null) {
      this.props.onFocus();
    }
  }

  handleFocusLost (event: React.FocusEvent<HTMLInputElement>): void {
    this._intervalId2 = setTimeout(() => {
      if (this.state.ignoreSetChange) {
        this.setState({ ignoreSetChange: false });
      } else {
        this.handleChanged({
          hasFocus: false,
          showDropDown: false,
          keyboardSelection: -1,
          ignoreSetChange: false
        });

        if (this.props.onBlur != null) {
          this.props.onBlur();
        }
      }
      this._intervalId2 = null;
    }, 100);
  }

  handleDropdownGetFocus (event: React.FocusEvent<HTMLUListElement>): void {
    this.setState({ ignoreSetChange: true });
  }

  handleMouseDown (event: React.MouseEvent<HTMLInputElement>): void {
    this.setState({ showDropDown: false, keyboardSelection: -1 });
  }

  selectKeyword (keyword: string): void {
    this.setState((prevState: State, props: Props) => {
      const searchString: string = _getSearchText(prevState, props.value);
      const filter: Filter = Filter.createFromRules([
        FilterRule.createRule(keyword, searchString, FilterRule.CONTAINS)
      ]);

      props.onSearch(filter.toString());

      return { searchText: null, showDropDown: false, keyboardSelection: -1 };
    });
  }

  fixDropDownSize () {
    const { id } = this.props;
    if ($('#' + id + '_wrapper').attr('class')?.includes('esko_open')) {
      let pageContainer = $('.esko_pagecontainer-nopadding');
      if (pageContainer[0] == null) {
        pageContainer = $('.esko_pagecontainer');
      }

      const bottom = ($('#' + id).offset()?.top ?? 0) + ($('#' + id).outerHeight(true) ?? 0);
      const spaceBelowButton = ($(window).height() ?? 0) - bottom - 4;

      $('#' + id + '_menu').css('max-height', spaceBelowButton - 25);
      $('#' + id + '_menu').css('overflow-y', 'auto');
    }
  }

  override render (): React.ReactNode {
    const {
      id, className, value,
      highlight, readOnly,
      alignRight, keywords
    } = this.props;

    const searchString: string = _getSearchText(this.state, value);

    return (
      <div
        id={id + '_wrapper'}
        className={classNames('esko_dropdown', 'esko_searchfield', {
          'esko_open': this.state.showDropDown,
          highlight: highlight && value !== '' && !this.state.hasFocus
        })}
      >
        <div
          className={classNames(
            'esko_input-group',
            'esko_search',
            className,
            { focus: this.state.hasFocus }
          )}
        >
          <span className='esko_input-group-addon'>
            <span className='bi bi-search' />
          </span>
          <input id={id}
            type='search'
            className='esko_form-control esko_input-expand'
            placeholder={translate('shared.placeholder.search')}
            value={searchString}
            onChange={this.handleSearchStringChange}
            onKeyDown={this.handleKeyPressed}
            onFocus={this.handleGetFocus}
            onMouseDown={this.handleMouseDown}
            onBlur={this.handleFocusLost}
            autoComplete='off'
            readOnly={readOnly}
          />
          <div className='esko_input-group-btn'>
            <button id={id + '_clear'}
              className={classNames('esko_btn', 'esko_btn-default', {
                hidden: searchString.length === 0
              })}
              type='button'
              onClick={() => this.clearSearchText()}
              disabled={readOnly}
            >
              <span className='bi bi-x-circle-fill' />
            </button>
          </div>
        </div>
        <ul
          id={id + '_menu'}
          className={classNames('esko_dropdown-menu', {'esko_dropdown-menu-right': alignRight})}
          onFocus={this.handleDropdownGetFocus}
        >
          {keywords.map((keyword, index) => {
            return (
              <li
                key={keyword + index}
                className={classNames({
                  active: this.state.keyboardSelection === index
                })}
              >
                <a
                  id={id + '_label_' + keyword}
                  role='button'
                  href='#'
                  onClick={(event) => {
                    event.preventDefault();
                    this.selectKeyword(keyword);
                    const input = document.getElementById(id);
                    if (input != null) {
                      input.focus();
                    }
                  }}
                >
                  <b>{translate('shared.filter.label.' + keyword)}: </b>"{searchString}"
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
}

export default SearchField;
